import React, { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router';
import { Auth } from 'aws-amplify';

import {
  Button,
  TextField,
  Box,
  Typography,
  Container,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

import Colors from '../../helpers/Colors';
import Assets from '../../helpers/assets';
import PasswordInput from './PasswordInput';
import { AppContext } from '../../contexts/AppContext';
import {saveToStorage} from "../../services/LocalStorage";
import {KEY_HAS_ONGOING_ADMIN_LOGIN} from "../../helpers/Constants";

export default function Login({showSSOOnly = false}) {
  const [username, setUsername] = useState(null);
  const [password, setPassword] = useState(null);
  const [error, setError] = useState(null);
  const [isRequestSent, setRequestSent] = useState(false);

  const { authData, setAuth } = useContext(AppContext);
  const history = useHistory();

  const classes = useStyles();

  useEffect(() => {
    if (authData) {
      history.replace("/kitchenselect");
    }
  }, []);

  const onClickLogin = () => {

    const trimmedUsername = username ? username.trim() : '';
    const trimmedPassword = password ? password.trim() : '';

    if (!trimmedUsername || !trimmedPassword) {
      setError('Please check your login credentials');
    }
    if (trimmedUsername && trimmedPassword && !isRequestSent) {
      setRequestSent(true);
      setError(null);
      Auth.signIn(trimmedUsername, trimmedPassword)
      .then((response) => {
        setRequestSent(false);
        const userAuthData = {
          username: response.username,
          userDataKey: response.userDataKey,
          idTokenPayload: response.signInUserSession.idToken.payload,
        };
        localStorage.setItem('dashboard_user', JSON.stringify({...userAuthData}));
        setAuth({...userAuthData});
        // setLoggedIn(true);
        history.replace("/kitchenselect");
        // routerPush('/dispatcher/dashboard');
      })
      .catch((error) => {
        setError(error.message);
        setRequestSent(false);
      })
    }
  }

  const handleAzureSSO = async () => {
    try {
      setRequestSent(true);
      setError(undefined);
      saveToStorage(KEY_HAS_ONGOING_ADMIN_LOGIN, true)
      await Auth.federatedSignIn({
        customProvider: "grubtech-azure-ad",
      })
    } catch (e) {
      saveToStorage(KEY_HAS_ONGOING_ADMIN_LOGIN, false)
      console.error("error during Azure SSO login", e);
      setError("Azure SSO login failed. Please try again");
      setRequestSent(false);
    }
  };

  return (
      <Box className={classes.template}
           style={{
             backgroundColor: showSSOOnly ? "#ffffff" : "rgba(34,147,249)",
             backgroundImage: `url(${showSSOOnly ? "none"
                 : Assets.loginBackground.default})`,
           }}>
        <Container
            component="main"
            className={classes.container}
        >
          <img
              src={showSSOOnly ? Assets.logoBlack.default
                  : Assets.logoWhite.default}
              alt=""
              className={classes.logo}
          />
          {!showSSOOnly && <Box className={classes.titleContainer}>
            <Typography className={classes.title}>
              Delivery Dashboard
            </Typography>
          </Box>}
          {showSSOOnly ? (<Button
              onClick={() => handleAzureSSO()}
              style={{
                backgroundColor: "#0652dd",
                color: "white",
                padding: "10px 28px",
                textTransform: "none",
                fontSize: 16,
                fontWeight: 500,
                boxShadow: "none",
                marginTop: "50px"
              }}
              disableElevation
          >
            Login with Grubtech AD
          </Button>) : (
              <Box className={classes.paper}><Box className={classes.form}
                                                  noValidate>
                <TextField
                    variant="outlined"
                    margin="normal"
                    placeholder="Enter Username"
                    fullWidth
                    onChange={(event) => setUsername(event.target.value)}
                    id="username"
                    label="USERNAME"
                    name="username"
                    className={classes.usernameInput}
                    autoFocus
                />
                <PasswordInput
                    onChange={(value) => setPassword(value)}
                />

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    className={classes.submit}
                    onClick={onClickLogin}
                >
                  {isRequestSent ? (
                      <CircularProgress size={22} color="white"/>
                  ) : (
                      "LOGIN"
                  )}
                </Button>
              </Box></Box>
          )}
          {error && (
              <Box className={ classes.errorContainer }>
                <Typography className={ classes.errorText }>
                  { error }
                </Typography>
                <img
                    src={ Assets.error }
                    alt=""
                    className={ classes.errorIcon }
                />
              </Box>
          )}
        </Container>
      </Box>
  );
}


const useStyles = makeStyles((theme) => ({
  template: {
    minHeight: 600,
    height: '100vh',
    width: '100vw',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(34, 147, 249)',
    backgroundImage: `url(${Assets.loginBackground.default})`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    margin: 0,
    padding: 0,
  },
  container: {
    marginTop: '8vh',
    height: '70vh',
    width: '40vw',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  paper: {
    width: '40vw',
    padding: 30,
    borderRadius: 8,
    maxWidth: 600,
    minWidth: 400,

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'white',
    minHeight: 250,

  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  usernameInput: {
    fontFamily: 'RobotoCondensed-Bold',
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: Colors.blue,
    color: Colors.white,
    height: 45,
    fontSize: 18,
    boxShadow: 'none',
    fontFamily: 'SFProDisplay-Medium',
    '&:hover': {
      backgroundColor: Colors.blue,
      boxShadow: 'none',
    }
  },
  logo: {
    height: 50,
    width: '30vw',
    objectFit: 'contain',
    marginBottom: 10,
  },
  title: {
    fontSize: 22,
    color: Colors.white,
    fontFamily: 'SFProDisplay-Medium',
  },
  errorText: {
    color: Colors.white,
    fontSize: 17,
    fontFamily: 'SFProDisplay-Regular',
  },
  titleContainer: {
    padding: 5,
    paddingLeft: 40,
    paddingRight: 40,
    backgroundColor: Colors.blue,
    borderRadius: 22,
    marginTop: 5,
    marginBottom: 20,
  },
  alertBox: {
    marginTop: 50,
  },
  errorIcon: {
    height: 22,
    width: 22,
    objectFit: 'contain',
    marginLeft: 20,
  },
  errorContainer: {
    borderRadius: 13,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    backgroundColor: '#DD2222',
    marginTop: 35,
    paddingLeft: 25,
    paddingRight: 25,
    paddingTop: 10,
    paddingBottom: 10,
  },
}));